import { useConversions } from "@/features/dashboard/queries/conversions.get.queries";
import { Batch } from "@/types/dashboard.types";
import { InfiniteData } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState } from "react";

interface HistoryContextType {
  history:
    | InfiniteData<
        {
          batches: Batch[];
          count: number;
        },
        unknown
      >
    | undefined;
  isPending: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
}

const HistoryContext = createContext<HistoryContextType | undefined>(undefined);

export function HistoryProvider({ children }: { children: React.ReactNode }) {
  const [history, setHistory] = useState<
    | InfiniteData<
        {
          batches: Batch[];
          count: number;
        },
        unknown
      >
    | undefined
  >(undefined);

  const {
    data,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useConversions();

  useEffect(() => {
    if (data) {
      setHistory(data);
    }
  }, [data]);

  return (
    <HistoryContext.Provider
      value={{
        history,
        isPending,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
      }}
    >
      {children}
    </HistoryContext.Provider>
  );
}

export function useHistory() {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error("useHistory must be used within a HistoryProvider");
  }
  return context;
}
